import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import { Employee } from "@/types/v2";
import { Button } from "@/components/Base/Button";

interface ReactivateEmployeeDialogProps {
  employee: Employee;
  open: boolean;
  setOpen: (arg: boolean) => void;
  /**
   * Runs the activation. `resetPassword: false` only re-enables the existing
   * account. The caller owns success/error toasts and cache updates.
   */
  onActivate: (resetPassword: boolean) => Promise<void>;
}

export const ReactivateEmployeeDialog = ({
  employee,
  open,
  setOpen,
  onActivate,
}: ReactivateEmployeeDialogProps) => {
  // The ref blocks a second click before React re-renders; the state is what
  // disables the buttons.
  const inFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChoice = async (resetPassword: boolean) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      await onActivate(resetPassword);
    } catch {
      // The caller reports failures; the dialog closes either way.
    }
    inFlight.current = false;
    setSubmitting(false);
    setOpen(false);
  };

  return (
    // Dismissing mid-write would look like a cancel while the activation still
    // goes through, so ignore every close request until it finishes.
    <Dialog onOpenChange={(next) => !submitting && setOpen(next)} open={open}>
      <DialogContent className="max-w-screen-md">
        <DialogHeader>
          <DialogTitle>Re-activate Team Member</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-base">
          {`You are about to re-activate ${employee.firstName} ${employee.lastName}. Do you want to reset their password?`}
        </DialogDescription>
        <DialogDescription>
          Keeping the current password only re-enables their account: they can
          sign in with the password they already have, and no email is sent.
          Resetting generates a new password and emails it to them.
        </DialogDescription>
        <DialogFooter>
          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleChoice(true)}
              disabled={submitting}
            >
              Reset password & email it
            </Button>
            <Button
              onClick={() => handleChoice(false)}
              loading={submitting}
              disabled={submitting}
            >
              Keep current password
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
